import { useImageGalleryTable } from '@components/common/imageGallery/hooks/imageGallery.hooks';
import Modal from '@components/common/modal/Modal';
import ImageModal from '@components/image/modal/ImageModal';
import { isArrayEmpty, SelectInput, t } from '@hclinear/hc-linear-react';
import { Upload } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { selectedElementSelector } from '@redux/store/layout-editor/layoutEditor.selector';
import { layoutEditorActions } from '@redux/store/layout-editor/layoutEditorSlice';
import { useSelector } from '@redux/store/store';
import { fitDimensionsWithinBounds } from '@util/box.util';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  elementTypeMap,
  imageElementTypeMap,
  ImageGallery,
  imageTypeSelectOptions,
  layoutEditorImageModalColumns,
} from '@/components';
import {
  imageResponseImageFileMapper,
  loadImageQueryOptions,
  useListImageQuery,
} from '@/hooks';
import { ElementTypeDTO, ImageDTO, ImageResizeType, ImageTypeDTO } from '@/model';
import { loadImageByIdAPI } from '@/service';
import { FormFieldType, ModalProps } from '@/type';

interface LayoutEditorImageModalProps extends ModalProps {
  elementType: ElementTypeDTO;
  modalType: 'add' | 'edit';
}

const LayoutEditorImageModal = ({
  open,
  handleClose,
  elementType,
  modalType,
}: LayoutEditorImageModalProps) => {
  const dispatch = useDispatch();

  const layoutId = useSelector((state) => state.layoutEditor.layoutId);
  const fetchedLayout = useSelector((state) => state.layoutEditor.fetchedLayout);

  const selectableElementTypes = imageElementTypeMap.get(elementType);
  const [selectedElementTypes, setSelectedElementTypes] =
    useState<ImageTypeDTO[]>(selectableElementTypes);
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState<boolean>(false);

  const { data: fetchedImages, isLoading } = useListImageQuery(
    selectedElementTypes,
    layoutId,
  );

  const {
    selectedImageId,
    setSelectedImageId,
    filter,
    updateFilter,
    sortBy,
    sorting,
    updateSort,
    items,
  } = useImageGalleryTable(fetchedImages || [], layoutEditorImageModalColumns);

  const selectedElement = useSelector(selectedElementSelector);

  const onSubmit = async (id?: number) => {
    const imageId = id ?? selectedImageId;
    const { isSuccess, data } = await loadImageByIdAPI({
      id: Number(imageId),
    });

    if (isSuccess) {
      switch (modalType) {
        case 'add':
          return await onAddSubmit(imageId, data);
        case 'edit':
          return await onEditSubmit(imageId, data);
        default:
          throw new Error('Unknown modal type');
      }
    }
  };

  const onAddSubmit = async (imageId: number, image: ImageDTO) => {
    const { width, height } = fitDimensionsWithinBounds(
      image.width,
      image.height,
      fetchedLayout.displayWidth,
      fetchedLayout.displayHeight,
    );

    dispatch(
      layoutEditorActions.initializeElement({
        imageId,
        name: fetchedImages?.find((img) => Number(img.id) === Number(imageId))?.name,
        elementType,
        content: image.file,
        width,
        height,
        contentWidth: image.width,
        contentHeight: image.height,
        imageResizeType: ImageResizeType.FIX,
      }),
    );

    dispatch(layoutEditorActions.setFetchedImageElement(image));
    handleClose(true);
  };

  const onEditSubmit = async (imageId: number, image: ImageDTO) => {
    const { width, height } = fitDimensionsWithinBounds(
      image.width,
      image.height,
      fetchedLayout.displayWidth,
      fetchedLayout.displayHeight,
    );

    dispatch(
      layoutEditorActions.updateElementProperty({
        ...selectedElement,
        imageId,
        content: image?.file,
        width,
        height,
        contentWidth: image.width,
        contentHeight: image.height,
      }),
    );
    handleClose(true);
  };

  const handleSelect = (id: number) => {
    setSelectedImageId(id);
  };

  const renderPreSelectCustomAction = () => {
    if (isArrayEmpty(selectableElementTypes) || selectableElementTypes?.length === 1)
      return;
    return (
      <Box sx={{ width: '350px' }}>
        <SelectInput
          selectField={{
            type: FormFieldType.Select,
            name: 'imageTypesSelect',
            label: t('common.image-types'),
            options: imageTypeSelectOptions(selectableElementTypes),
            isMultiselect: true,
          }}
          // @ts-ignore
          field={{
            value: selectedElementTypes,
            onChange: setSelectedElementTypes,
          }}
        />
      </Box>
    );
  };

  const renderPostSelectCustomAction = () => {
    return (
      <Tooltip title={t('common.upload-new-image')}>
        <IconButton size={'medium'}>
          <Upload fontSize='large' onClick={() => setIsAddImageModalOpen(true)} />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={
          elementType === ElementTypeDTO.IMAGE_DISPLAY_NORMAL
            ? 'common.images'
            : 'enum.pager'
        }
        maxWidth={'1200px'}
        style={{ width: '1200px' }}
        dialogContentStyle={{
          paddingTop: 0,
        }}
      >
        <ImageGallery
          items={items}
          fullSizeImageQueryOptions={(id: number) => ({
            ...loadImageQueryOptions(id, false),
            select: imageResponseImageFileMapper,
          })}
          onSelect={handleSelect}
          selectedId={selectedImageId}
          filter={filter}
          onSubmit={onSubmit}
          updateFilter={updateFilter}
          sortDirection={sorting[0]?.desc ? 'desc' : 'asc'}
          sortBy={sortBy}
          updateSort={updateSort}
          isLoading={isLoading}
          preSelectCustomAction={renderPreSelectCustomAction()}
          postSelectCustomAction={renderPostSelectCustomAction()}
        />
      </Modal>
      {isAddImageModalOpen && (
        <ImageModal
          open={isAddImageModalOpen}
          handleClose={() => setIsAddImageModalOpen(false)}
          elementType={elementTypeMap.get(elementType)}
        />
      )}
    </>
  );
};

export default LayoutEditorImageModal;
